/**
 * WorkspaceRail — the focus-state right rail (doc 147 §5.3 redesign · task #10).
 *
 * Renders when the workspace is in 'focused' mode. Summarises the focal
 * entity: name, location, schema head, reference counts, and the primary
 * "Open ⏎" CTA (navigates to the entity's full-page detail screen). Field
 * rows that reference another entity become click-to-walk buttons.
 *
 * Pure presentational — no Firebase, no router. Receives a manifest plus the
 * focused entity id and an action bag.
 */
import type { AtlasManifest, DomainLayer } from '@atlas-manifest/types';
import {
  entityById,
  domainById,
  entityDegree,
  entityMetadata,
} from '../state/selectors.js';
import { Icon } from '../components/Icon.js';
import { DomainBadge } from './DomainBadge.js';
import { LayerBadge } from './LayerBadge.js';

export interface WorkspaceRailProps {
  manifest: AtlasManifest;
  entityId: string;
  pinned: string[];
  onOpenSheet: () => void;
  onTogglePin: (entityId: string) => void;
  onNavigateEntity: (entityId: string) => void;
}

const VISIBLE_FIELD_LIMIT = 6;

export function WorkspaceRail({
  manifest,
  entityId,
  pinned,
  onOpenSheet,
  onTogglePin,
  onNavigateEntity,
}: WorkspaceRailProps): React.JSX.Element | null {
  const entity = entityById(manifest, entityId);
  if (!entity) return null;

  const domain = domainById(manifest, entity.domain);
  const meta = entityMetadata(manifest, entityId);
  const degree = entityDegree(manifest, entityId);
  const visibleFields = entity.fields.slice(0, VISIBLE_FIELD_LIMIT);
  const overflowFields = Math.max(0, entity.fields.length - VISIBLE_FIELD_LIMIT);
  const isPinned = pinned.includes(entityId);

  return (
    <aside className="workspace-rail" role="complementary" aria-label={`Selected entity: ${entity.name}`}>
      <header className="workspace-rail-head">
        <div className="section-label">SELECTED</div>
        <button
          className="rail-pin"
          aria-pressed={isPinned}
          aria-label={isPinned ? 'Unpin' : 'Pin'}
          onClick={() => onTogglePin(entityId)}
        >
          <Icon name="push_pin" size="sm" color={isPinned ? 'var(--brand-subtle)' : undefined} />
        </button>
      </header>

      <h2
        className="workspace-rail-name"
        style={{ color: domain ? `var(${domain.color})` : undefined }}
      >
        {entity.name}
      </h2>
      <div className="workspace-rail-meta" style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
        {domain && (
          <>
            <DomainBadge domainId={domain.id} size="sm" />
            <LayerBadge layer={(domain.layer as DomainLayer) ?? 'L5'} size="sm" />
            <span>{domain.name}</span>
          </>
        )}
        {entity.docSection && <span> · {entity.docSection}</span>}
      </div>
      <div className="workspace-rail-path mono">
        {entity.firestorePath ?? <em>ephemeral · not persisted</em>}
      </div>

      <div className="section-label" style={{ marginTop: 'var(--sp-md)' }}>
        FIELDS · {entity.fields.length}
      </div>
      <ul className="workspace-rail-fields">
        {visibleFields.map((field) => {
          if (field.referencesEntity) {
            const target = field.referencesEntity;
            return (
              <li key={field.name}>
                <button
                  className="workspace-rail-field workspace-rail-field-ref"
                  onClick={() => onNavigateEntity(target)}
                >
                  <span className="mono">{field.name}</span>
                  <span className="mono ref-arrow">→ {target}</span>
                </button>
              </li>
            );
          }
          return (
            <li key={field.name} className="workspace-rail-field">
              <span className="mono">{field.name}</span>
              <span className="mono type">{field.type}</span>
            </li>
          );
        })}
        {overflowFields > 0 && (
          <li className="workspace-rail-field type">+{overflowFields} more</li>
        )}
      </ul>

      <div className="section-label" style={{ marginTop: 'var(--sp-md)' }}>
        REFS · {degree.outDegree} out · {degree.inDegree} in
      </div>
      {meta.tierGate && (
        <div className="workspace-rail-chip workspace-rail-chip-warn">
          {meta.tierGate}{meta.packGate ? ` · ${meta.packGate}` : ''}
        </div>
      )}

      <button
        className="btn btn-primary workspace-rail-open"
        onClick={onOpenSheet}
      >
        Open ⏎
      </button>
    </aside>
  );
}
